// Vendor Libs
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

// Components
import {useDispatch, useSelector} from 'react-redux';
import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import Form from '../core/form';
import Permissions from '../core/permissions';

// Styles
import './ad-insertion-form.scss';
import Icon from '../core/icon';

// Redux
import {servicesActionPropertiesActions} from '../../actions/services-action-caller-actions';

function AdInsertionConfigForm({editing}) {
    const dispatch = useDispatch();
    const {content} = useSelector((state) => state?.servicesActionData?.responsePropertiesGet?.data || []);
    const [counter, setCounter] = useState(0);
    const [caidDefault, setCaidDefault] = useState('');
    const [csidPrefixDefault, setCsidPrefixDefault] = useState('');
    const [csidSuffixDefault, setCsidSuffixDefault] = useState('');
    const [sfidPrefixDefault, setSfidPrefixDefault] = useState('');
    const [sfidSuffixDefault, setSfidSuffixDefault] = useState('');
    const [caid, setCaid] = useState('');
    const [csidPrefix, setCsidPrefix] = useState('');
    const [csidSuffix, setCsidSuffix] = useState('');
    const [sfidPrefix, setSfidPrefix] = useState('');
    const [sfidSuffix, setSfidSuffix] = useState('');
    const [origCaid, setOrigCaid] = useState('');
    const [origCsidPrefix, setOrigCsidPrefix] = useState('');
    const [origCsidSuffix, setOrigCsidSuffix] = useState('');
    const [origSfidPrefix, setOrigSfidPrefix] = useState('');
    const [origSfidSuffix, setOrigSfidSuffix] = useState('');
    const [fullProperties, setFullProperties] = useState([]);
    const [isEditMode, setEditMode] = useState(false);
    const counterLimit = 5;
    const adInsertionVariables = [
        {name: 'caid', description: 'CAID', value: caid},
        {name: 'csidPrefix', description: 'CSID Site Prefix', value: csidPrefix},
        {name: 'csidSuffix', description: 'CSID Site Suffix', value: csidSuffix},
        {name: 'sfidPrefix', description: 'SFID Site Prefix', value: sfidPrefix},
        {name: 'sfidSuffix', description: 'SFID Site Suffix', value: sfidSuffix},
    ];

    useEffect(() => {
        if (content && content.length) {
            setFullProperties(content);
            const res = content.map((c) => ({
                key: c.key,
                value: c.value,
                defaultValue: c.defaultValue,
                applicationPropertyId: c.applicationPropertyId,
            }));

            const csidPrefixFull = res.find((r) => r.key.includes('ad-insertion.csid.prefix')) || '';
            const csidSuffixFull = res.find((r) => r.key.includes('ad-insertion.csid.suffix')) || '';
            const sfidPrefixFull = res.find((r) => r.key.includes('ad-insertion.sfid.prefix')) || '';
            const sfidSuffixFull = res.find((r) => r.key.includes('ad-insertion.sfid.suffix')) || '';
            const caidFull = res.find((r) => r.key.includes('ad-insertion.caid')) || '';

            setCsidPrefixDefault(csidPrefixFull?.defaultValue);
            setCsidSuffixDefault(csidSuffixFull?.defaultValue);
            setSfidPrefixDefault(sfidPrefixFull?.defaultValue);
            setSfidSuffixDefault(sfidSuffixFull?.defaultValue);
            setCaidDefault(caidFull?.defaultValue);

            setCsidPrefix(csidPrefixFull?.value);
            setCsidSuffix(csidSuffixFull?.value);
            setSfidPrefix(sfidPrefixFull?.value);
            setSfidSuffix(sfidSuffixFull?.value);
            setCaid(caidFull?.value);

            setOrigCaid(caidFull?.value);
            setOrigCsidPrefix(csidPrefixFull?.value);
            setOrigCsidSuffix(csidSuffixFull?.value);
            setOrigSfidPrefix(sfidPrefixFull?.value);
            setOrigSfidSuffix(sfidSuffixFull?.value);
            setCounter(0);
        } else if (counter < counterLimit) {
            setCounter(counter + 1);
            dispatch(servicesActionPropertiesActions({method: 'get'}));
        } else {
            setCaid('');
            setCsidPrefix('');
            setCsidSuffix('');
            setSfidPrefix('');
            setSfidSuffix('');
            setCounter(counterLimit);
        }
    }, [content]);

    const updateAddDbRecord = (key, value, defaultValue) => {
        const id = fullProperties.find((r) => r.key.includes(key))?.applicationPropertyId;
        if (id) {
            dispatch(
                servicesActionPropertiesActions({
                    applicationPropertyId: id,
                    key,
                    value,
                    defaultValue,
                    method: 'put',
                })
            );
        } else {
            dispatch(
                servicesActionPropertiesActions({
                    key,
                    value,
                    defaultValue,
                    method: 'post',
                })
            );
        }
    };

    const handleChange = (fieldName, valueInput) => {
        const value = valueInput.toString().length ? valueInput : null;
        if (fieldName === 'sfidSuffix') {
            setSfidSuffix(value);
        } else if (fieldName === 'sfidPrefix') {
            setSfidPrefix(value);
        } else if (fieldName === 'csidSuffix') {
            setCsidSuffix(value);
        } else if (fieldName === 'csidPrefix') {
            setCsidPrefix(value);
        } else if (fieldName === 'caid') {
            setCaid(value);
        }
    };

    const showField = (variable, index) => (
        <div className="p-field" key={index}>
            <label className="p-d-block" htmlFor={variable.name}>
                {variable.description}
            </label>
            <InputText
                className="p-d-block inputTimer"
                disabled={!isEditMode}
                id={variable.name}
                value={variable.value ? variable.value : ''}
                onChange={(e) => handleChange(variable.name, e.target.value)}
            />
        </div>
    );

    const saveModified = () => {
        setEditMode(false);
        updateAddDbRecord('ad-insertion.csid.prefix', csidPrefix, csidPrefixDefault);
        updateAddDbRecord('ad-insertion.csid.suffix', csidSuffix, csidSuffixDefault);
        updateAddDbRecord('ad-insertion.sfid.prefix', sfidPrefix, sfidPrefixDefault);
        updateAddDbRecord('ad-insertion.sfid.suffix', sfidSuffix, sfidSuffixDefault);
        updateAddDbRecord('ad-insertion.caid', caid, caidDefault);
        setCounter(0);
        dispatch(servicesActionPropertiesActions({method: 'get'}));
    };

    const setDefaultValues = () => {
        setCsidPrefix(csidPrefixDefault);
        setCsidSuffix(csidSuffixDefault);
        setSfidPrefix(sfidPrefixDefault);
        setSfidSuffix(sfidSuffixDefault);
        setCaid(caidDefault);
    };

    return (
        <Permissions
            accessRequirement="update"
            elementService="vfb"
            notAuthorizedMessage={true}
            requiresServiceAccess={true}
        >
            {(content && content.length) || counter >= counterLimit ? (
                <Form className="ad-insertion-form">
                    <div className="p-d-flex p-flex-column">
                        {adInsertionVariables.map((timerVar, index) => showField(timerVar, index))}
                    </div>
                    {editing && (
                        <div className="ad-insertion-form--controls">
                            <div className="p-d-flex p-flex-row p-jc-left">
                                <Button
                                    className={
                                        isEditMode
                                            ? 'p-button-raised p-button-secondary'
                                            : 'p-button-raised p-button-primary'
                                    }
                                    onClick={() => {
                                        setEditMode(!isEditMode);
                                        if (isEditMode) {
                                            setCaid(origCaid);
                                            setCsidPrefix(origCsidPrefix);
                                            setCsidSuffix(origCsidSuffix);
                                            setSfidPrefix(origSfidPrefix);
                                            setSfidSuffix(origSfidSuffix);
                                        }
                                    }}
                                >
                                    {!isEditMode ? 'Make Changes' : 'Cancel'}
                                </Button>
                                {isEditMode && (
                                    <>
                                        <Button
                                            className="p-button-raised p-button-primary saveButton"
                                            onClick={() => saveModified()}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            className="p-button-raised p-button-info saveButton"
                                            onClick={() => setDefaultValues()}
                                        >
                                            Set Default Values
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </Form>
            ) : (
                <Icon fontType="fa" iconName="spinner" spin={true} style={{color: '#07619c', padding: 10}} />
            )}
        </Permissions>
    );
}

AdInsertionConfigForm.propTypes = {
    editing: PropTypes.bool,
};

export default AdInsertionConfigForm;
